import { FC } from 'react';
import { ResponsiveSankey } from '@nivo/sankey';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { ReportComponent } from '@/types';

import { humanizeChartLabel, truncateChartLabel } from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { useNivoReportData } from '../useNivoReportData';
import { useNivoReportFormatting } from '../useNivoReportFormatting';

interface SankeyChartProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

const SankeyChart: FC<SankeyChartProps> = ({ component, numerator, denominator }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();

    const data = {
        nodes: [
            {
                id: 'John',
                nodeColor: token.red6,
            },
            {
                id: 'Raoul',
                nodeColor: token.purple6,
            },
            {
                id: 'Jane',
                nodeColor: token.blue6,
            },
            {
                id: 'Marcel',
                nodeColor: token.green6,
            },
            {
                id: 'Ibrahim',
                nodeColor: token.magenta6,
            },
            {
                id: 'Junko',
                nodeColor: token.cyan6,
            },
        ],
        links: [
            {
                source: 'Marcel',
                target: 'Ibrahim',
                value: 125,
            },
            {
                source: 'Marcel',
                target: 'Raoul',
                value: 191,
            },
            {
                source: 'Marcel',
                target: 'John',
                value: 45,
            },
            {
                source: 'Ibrahim',
                target: 'Jane',
                value: 5,
            },
            {
                source: 'Ibrahim',
                target: 'John',
                value: 144,
            },
            {
                source: 'Ibrahim',
                target: 'Junko',
                value: 25,
            },
            {
                source: 'Jane',
                target: 'John',
                value: 141,
            },
            {
                source: 'Jane',
                target: 'Raoul',
                value: 168,
            },
            {
                source: 'Raoul',
                target: 'Junko',
                value: 168,
            },
            {
                source: 'Raoul',
                target: 'John',
                value: 176,
            },
            {
                source: 'Junko',
                target: 'John',
                value: 68,
            },
        ],
    };
    const chartData = useNivoReportData(component, data);
    const { formatMetricValue } = useNivoReportFormatting(component);

    return (
        <>
            <div
                style={{
                    aspectRatio: `${numerator}/${denominator}`,
                    height: '100%',
                    minHeight: 0,
                    width: '100%',
                }}
            >
                <ResponsiveSankey
                    data={chartData}
                    valueFormat={(value) => formatMetricValue(value)}
                    margin={{ top: 12, right: 80, bottom: 16, left: 80 }}
                    align='justify'
                    colors={colorsArray}
                    theme={nivoChartTheme}
                    nodeOpacity={1}
                    nodeHoverOthersOpacity={0.25}
                    nodeThickness={16}
                    nodeSpacing={24}
                    nodeInnerPadding={4}
                    nodeBorderWidth={1}
                    nodeBorderRadius={2}
                    nodeBorderColor={{
                        from: 'color',
                        modifiers: [['darker', 0.2]],
                    }}
                    linkOpacity={0.5}
                    linkHoverOthersOpacity={0.1}
                    linkContract={4}
                    enableLabels={true}
                    linkBlendMode='normal' // very important
                    enableLinkGradient={false}
                    label={(node) => truncateChartLabel(node.id, 14)}
                    labelPosition='outside'
                    labelOrientation='horizontal'
                    labelPadding={8}
                    labelTextColor={token.colorTextSecondary}
                    nodeTooltip={({ node }) => (
                        <CustomNivoChartTooltip
                            title={humanizeChartLabel(node.label)}
                            entries={[
                                {
                                    color: node.color,
                                    label: 'Total',
                                    value: node.value,
                                },
                            ]}
                            formatValue={formatMetricValue}
                        />
                    )}
                    linkTooltip={({ link }) => (
                        <CustomNivoChartTooltip
                            title={`${humanizeChartLabel(link.source.label)} -> ${humanizeChartLabel(link.target.label)}`}
                            entries={[
                                {
                                    color: link.color,
                                    label: 'Value',
                                    value: link.value,
                                },
                            ]}
                            formatValue={formatMetricValue}
                        />
                    )}
                    legends={[]}
                />
            </div>
        </>
    );
};

export default SankeyChart;
